import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { FileText, Upload, RefreshCw, Trash2, CheckCircle, Clock, ExternalLink } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  submitted: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  live: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  rejected: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
};

const PLATFORM_OPTIONS = [
  { id: 'craigslist', label: 'Craigslist', reach: 'Very High' },
  { id: 'olx', label: 'OLX', reach: 'High' },
  { id: 'gumtree', label: 'Gumtree', reach: 'High' },
  { id: 'locanto', label: 'Locanto', reach: 'Medium' },
  { id: 'adpost', label: 'Adpost', reach: 'Medium' },
  { id: 'oodle', label: 'Oodle', reach: 'Low' },
];

export default function ClassifiedSubmissionsPage() {
  const { selectedProjectId } = useProject();
  const [activeTab, setActiveTab] = useState('create');
  const [submissions, setSubmissions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  // Form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [price, setPrice] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [keywords, setKeywords] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['craigslist', 'olx']);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const loadSubmissions = async () => {
    try {
      const { data } = await api.get('/classified-submissions/');
      setSubmissions(data.submissions || []);
    } catch {
      // silent
    }
  };

  const loadPlatforms = async () => {
    try {
      const { data } = await api.get('/classified-submissions/platforms');
      setPlatforms(data.platforms || []);
    } catch {
      // silent
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/classified-submissions/stats');
      setStats(data);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    loadSubmissions();
    loadPlatforms();
    loadStats();
  }, []);

  const togglePlatform = (id) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (selectedPlatforms.length === 0) {
      setStatus('Error: Select at least one platform.');
      return;
    }
    setLoading(true);
    setStatus('Creating submission...');
    try {
      await api.post('/classified-submissions/', {
        title,
        description,
        website_url: websiteUrl || null,
        contact_email: contactEmail || null,
        price: price || null,
        location: location || null,
        category: category || null,
        keywords: keywords.split(',').map((k) => k.trim()).filter(Boolean),
        platforms: selectedPlatforms,
        project_id: selectedProjectId || undefined,
      });
      setStatus('Submission created successfully!');
      setTitle('');
      setDescription('');
      setWebsiteUrl('');
      setContactEmail('');
      setPrice('');
      setLocation('');
      setCategory('');
      setKeywords('');
      setSelectedPlatforms(['craigslist', 'olx']);
      await loadSubmissions();
      await loadStats();
      setActiveTab('submissions');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const markPlatform = async (submissionId, platform, newStatus) => {
    try {
      await api.patch(`/classified-submissions/${submissionId}/platform/${platform}`, { status: newStatus });
      await loadSubmissions();
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    }
  };

  const deleteSubmission = async (id) => {
    try {
      await api.delete(`/classified-submissions/${id}`);
      await loadSubmissions();
      await loadStats();
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    }
  };

  const tabs = [
    { id: 'create', label: 'Create' },
    { id: 'submissions', label: `Submissions (${submissions.length})` },
    { id: 'platforms', label: 'Platforms' },
  ];

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
          <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Classified Submissions</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Submit classified ads to Craigslist, OLX, Gumtree, Locanto and more for off-page SEO and lead generation.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Submissions', value: submissions.length },
          { label: 'Live', value: submissions.filter((s) => s.overall_status === 'live').length },
          { label: 'Submitted', value: submissions.filter((s) => s.overall_status === 'submitted').length },
          { label: 'Pending', value: submissions.filter((s) => s.overall_status === 'pending').length },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stat.value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`pb-2 px-1 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-orange-600 text-orange-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        {/* CREATE TAB */}
        {activeTab === 'create' && (
          <form onSubmit={handleCreate} className="space-y-5 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Ad Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Professional SEO Services for Local Businesses"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="We offer full-service SEO including keyword research, on-page optimization, and link building..."
                rows={4}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Website URL</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yoursite.com"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contact@yoursite.com"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Price</label>
                <input
                  type="text"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="$500/month"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Location</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="New York, NY"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Services"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Keywords <span className="text-xs text-slate-400">(comma separated)</span>
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="seo services, local seo, digital marketing"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Target Platforms <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {PLATFORM_OPTIONS.map((p) => (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedPlatforms.includes(p.id)
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(p.id)}
                      onChange={() => togglePlatform(p.id)}
                      className="accent-orange-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{p.label}</p>
                      <p className="text-xs text-slate-400">{p.reach} reach</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-orange-600 text-white px-5 py-2 rounded-md text-sm hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              {loading ? 'Submitting...' : 'Create Submission'}
            </button>
          </form>
        )}

        {/* SUBMISSIONS TAB */}
        {activeTab === 'submissions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">{submissions.length} total submissions</p>
              <button
                type="button"
                onClick={loadSubmissions}
                className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {submissions.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No submissions yet. Create your first classified ad.</p>
              </div>
            ) : (
              submissions.map((sub) => (
                <div key={sub.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{sub.title}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{sub.description}</p>
                      {sub.location && (
                        <p className="text-xs text-slate-400 mt-0.5">{sub.location}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[sub.overall_status] || STATUS_STYLES.pending}`}>
                        {sub.overall_status}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteSubmission(sub.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {sub.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {sub.keywords.map((kw) => (
                        <span key={kw} className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {sub.platforms?.map((pt) => (
                      <div key={pt.platform} className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 rounded-md p-2">
                        <div>
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{pt.name || pt.platform}</p>
                          <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${STATUS_STYLES[pt.status] || STATUS_STYLES.pending}`}>
                            {pt.status}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {pt.status === 'pending' && (
                            <button
                              type="button"
                              title="Mark Submitted"
                              onClick={() => markPlatform(sub.id, pt.platform, 'submitted')}
                              className="text-blue-400 hover:text-blue-600"
                            >
                              <Clock className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {pt.status === 'submitted' && (
                            <button
                              type="button"
                              title="Mark Live"
                              onClick={() => markPlatform(sub.id, pt.platform, 'live')}
                              className="text-green-400 hover:text-green-600"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {pt.live_url && (
                            <a
                              href={pt.live_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-orange-500"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* PLATFORMS TAB */}
        {activeTab === 'platforms' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              All supported classified ad directories for off-page SEO and local visibility.
            </p>
            {platforms.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {platforms.map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{p.name}</h3>
                      <div className="flex gap-3 mt-1 text-xs text-slate-500">
                        <span>Reach: <strong className="text-slate-700 dark:text-slate-300">{p.estimated_reach || '—'}</strong></span>
                        <span>DoFollow: <strong className="text-slate-700 dark:text-slate-300">{p.do_follow ? 'Yes' : 'No'}</strong></span>
                      </div>
                    </div>
                    {p.submit_url && (
                      <a
                        href={p.submit_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium shrink-0"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {PLATFORM_OPTIONS.map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{p.label}</h3>
                    <p className="text-xs text-slate-500 mt-1">Reach: {p.reach}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            status.startsWith('Error')
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200'
          }`}>
            {status}
          </div>
        )}
      </div>
    </motion.div>
  );
}
